/**
 * ``TemplatesList`` — v1-style grid of presentation templates.
 *
 * Port of the v1 ``app/settings/presentations/page.tsx`` grid:
 *   - 4-col desktop / 2-col tablet / 1-col mobile.
 *   - Each card ~240×320 with a 240×180 thumbnail + 32px footer.
 *   - Hover: ``shadow-md`` lift + ``bg-cream-50`` accent.
 *   - Scope chip top-right; "default" chip when ``effectiveDefault`` matches.
 *   - Custom rows expose a "Modifica" hover overlay → routes to the editor.
 *     Built-ins are read-only; clicking them surfaces a "Personalizza per
 *     modificare" CTA → duplicates the spec and lands on the new editor.
 *
 * Single-surface only — the StudioDrawer keeps its own kind-tabbed split
 * editor at ``/workspace/templates/$kind``. This grid is the canonical
 * "browse" surface for ``/workspace/templates`` (the index route).
 */

import * as React from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Sparkles, Wand2 } from "lucide-react";

import {
  type TemplateKind,
  type TemplateScope,
  type TemplateSummary,
  getTemplate,
} from "@ando/core";
import { Button, SurfaceEmptyState, cn } from "@ando/ui";

import { useCreateTemplate } from "../hooks/useTemplateMutations";
import { useTemplateGrid } from "../hooks/useTemplates";

const KINDS: ReadonlyArray<{
  readonly value: TemplateKind | "all";
  readonly label: ReturnType<typeof msg>;
}> = [
  { value: "all", label: msg`Tutti` },
  { value: "chrome", label: msg`Chrome` },
  { value: "paper", label: msg`Paper` },
  { value: "writing_style", label: msg`Voice` },
];

const SCOPE_CHIP: Record<
  TemplateScope,
  { readonly label: ReturnType<typeof msg>; readonly cls: string }
> = {
  builtin: { label: msg`BUILT-IN`, cls: "bg-ink-1/[0.05] text-ink-3" },
  user: { label: msg`USER`, cls: "bg-ando-orange-soft text-ando-orange" },
  org: { label: msg`ORG`, cls: "bg-amber-500/15 text-amber-700" },
  public: { label: msg`PUBBLICO`, cls: "bg-success/15 text-success" },
};

const KIND_LABEL: Record<TemplateKind, ReturnType<typeof msg>> = {
  chrome: msg`Chrome`,
  paper: msg`Paper`,
  writing_style: msg`Voice`,
};

export interface TemplatesListProps {
  /**
   * Initial selected scope filter; defaults to ``"all"``. Stored locally
   * because the route owns its filter state — no need to round-trip to a
   * URL search param for the MVP.
   */
  readonly initialKind?: TemplateKind | "all";
}

export const TemplatesList: React.FC<TemplatesListProps> = ({ initialKind = "all" }) => {
  const { t, i18n } = useLingui();
  const [activeKind, setActiveKind] = React.useState<TemplateKind | "all">(initialKind);
  const navigate = useNavigate();
  const createTpl = useCreateTemplate();

  const kindParam = activeKind === "all" ? undefined : activeKind;
  const { templates, isLoading, isError, effectiveDefault, refetch } = useTemplateGrid(kindParam);

  // Custom templates can be edited inline; built-ins fork via "Personalizza".
  const handleOpenEditor = React.useCallback(
    (slug: string) => {
      void navigate({
        to: "/workspace/templates/edit/$slug",
        params: { slug },
      });
    },
    [navigate],
  );

  /**
   * Fork a built-in into a fresh user-scope copy + open it for editing.
   * Pulls the source spec via the detail endpoint (the list response only
   * carries summaries) and posts a new template with a "(copia)" suffix.
   * If the create fails (most likely an authz error on built-ins with the
   * same slug already cloned), we surface nothing — the calling card stays
   * idempotent for the user's next click.
   */
  const handleForkBuiltin = React.useCallback(
    async (tpl: TemplateSummary) => {
      // Built-ins have no ``slug`` — the BE addresses them by ``name``.
      const identifier = tpl.slug ?? tpl.name;
      let spec: Record<string, unknown> | null = null;
      try {
        const detail = await getTemplate(identifier);
        spec = detail.spec;
      } catch {
        spec = null;
      }
      if (!spec) return;
      try {
        const created = await createTpl.mutateAsync({
          name: `${tpl.name} (copia)`,
          description: tpl.description || t`Copia personalizzabile del template built-in.`,
          kind: tpl.kind,
          spec,
        });
        if (created.slug) {
          handleOpenEditor(created.slug);
        }
      } catch {
        // Surface via the caller's toast layer; the grid stays in place.
      }
    },
    [createTpl, handleOpenEditor, t],
  );

  return (
    <div className="flex flex-col gap-4 px-8 py-6">
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => {
          const isActive = activeKind === k.value;
          return (
            <button
              key={k.value}
              type="button"
              onClick={() => setActiveKind(k.value)}
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 font-medium text-[12px] transition-colors",
                isActive
                  ? "bg-ink-1 text-white"
                  : "bg-card/60 text-ink-2 hover:bg-card hover:text-ink-1",
              )}
              aria-pressed={isActive}
            >
              {i18n._(k.label)}
            </button>
          );
        })}
      </div>

      {isError ? (
        <div className="flex flex-col items-start gap-2 rounded-md border border-hairline bg-card/40 p-4 text-ink-2 text-sm">
          <span>
            <Trans>Errore nel caricamento dei template.</Trans>
          </span>
          <Button size="xs" variant="outline" onClick={() => void refetch()}>
            <Trans>Riprova</Trans>
          </Button>
        </div>
      ) : isLoading ? (
        <div
          role="status"
          aria-label={t`Caricamento template…`}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-80 animate-pulse rounded-lg border border-hairline/40 bg-card/40"
            />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <SurfaceEmptyState
          icon={<Sparkles size={18} aria-hidden />}
          title={t`Nessun template`}
          description={t`Crea il primo template personalizzato dalla scheda Studio.`}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {templates.map((tpl) => (
            <TemplateGridCard
              key={`${tpl.kind}:${tpl.scope}:${tpl.slug ?? tpl.name}`}
              template={tpl}
              isDefault={effectiveDefault === tpl.name}
              isForking={createTpl.isPending}
              onEdit={tpl.slug ? () => handleOpenEditor(tpl.slug as string) : undefined}
              onFork={!tpl.slug ? () => void handleForkBuiltin(tpl) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};
TemplatesList.displayName = "TemplatesList";

interface TemplateGridCardProps {
  readonly template: TemplateSummary;
  readonly isDefault: boolean;
  readonly isForking: boolean;
  /** Custom templates only — opens the editor route. */
  readonly onEdit?: () => void;
  /** Built-ins only — clones into a user-scope copy. */
  readonly onFork?: () => void;
}

/** Single card: 240×320, 240×180 thumbnail, 32px footer w/ name + chip. */
const TemplateGridCard: React.FC<TemplateGridCardProps> = ({
  template,
  isDefault,
  isForking,
  onEdit,
  onFork,
}) => {
  const { t, i18n } = useLingui();
  const scope = SCOPE_CHIP[template.scope];
  const editable = onEdit != null;
  const primary = editable ? onEdit : onFork;
  return (
    <div
      className={cn(
        "group relative flex h-80 flex-col overflow-hidden rounded-lg border border-hairline/70 bg-card/70 transition-all",
        "hover:-translate-y-0.5 hover:bg-cream-50 hover:shadow-md",
        "focus-within:ring-2 focus-within:ring-accent/40",
      )}
    >
      <button
        type="button"
        onClick={primary}
        disabled={!editable && isForking}
        aria-label={editable ? t`Modifica ${template.name}` : t`Personalizza ${template.name}`}
        className="flex flex-1 flex-col text-left focus-visible:outline-none"
      >
        <div className="relative h-44 w-full bg-gradient-to-br from-ando-orange-soft/30 to-amber-500/15">
          {template.preview_url ? (
            <img
              src={template.preview_url}
              alt=""
              aria-hidden
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-ink-3">
              <Sparkles size={24} aria-hidden />
            </div>
          )}

          <span
            className={cn(
              "absolute top-2 right-2 rounded px-1.5 py-0.5 font-mono text-[9.5px] tracking-wider",
              scope.cls,
            )}
          >
            {i18n._(scope.label)}
          </span>

          {isDefault ? (
            <span className="absolute top-2 left-2 rounded bg-success/20 px-1.5 py-0.5 font-mono text-[9.5px] text-success tracking-wider">
              <Trans>DEFAULT</Trans>
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-1.5 px-3 py-2.5">
          <span className="truncate font-semibold text-[13.5px] text-ink-1">{template.name}</span>
          <span className="line-clamp-3 text-[11.5px] text-ink-3">{template.description}</span>
          <span className="mt-auto text-[10.5px] text-ink-3 uppercase tracking-wider">
            {i18n._(KIND_LABEL[template.kind])}
          </span>
        </div>

        {/* Hover overlay — custom rows show a pencil; built-ins show a wand. */}
        <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-3 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-ink-1 px-2.5 py-1 font-medium text-[11px] text-white shadow-sm">
            {editable ? (
              <>
                <Pencil size={11} aria-hidden />
                <Trans>Modifica</Trans>
              </>
            ) : (
              <>
                <Wand2 size={11} aria-hidden />
                <Trans>Personalizza per modificare</Trans>
              </>
            )}
          </span>
        </div>
      </button>
    </div>
  );
};
TemplateGridCard.displayName = "TemplateGridCard";
